import { Avatar, Badge, IconButton, cn, useToast } from '@nexo/ui';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Star, Trash2 } from 'lucide-react';
import { useDeferredValue, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../../lib/api';
import { formatRelative } from '../../lib/format';
import { TeamMembersDialog } from './AdminDetails';
import { AdminPage, DataTable, Pagination } from './AdminLayout';
import { SearchBox } from './AdminUsers';
import { errorMessage } from '../../lib/forms';

export function AdminTeams() {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const query = useDeferredValue(q.trim());
  const queryClient = useQueryClient();
  const toast = useToast();
  const [viewing, setViewing] = useState<{ id: string; name: string } | null>(null);
  const teams = useQuery({
    queryKey: ['admin', 'teams', query, page],
    queryFn: () => api.admin.teams({ q: query || undefined, page }),
    placeholderData: keepPreviousData,
  });
  const remove = useMutation({
    mutationFn: api.admin.deleteTeam,
    onSuccess: () => {
      toast({ title: t('Equipo eliminado'), tone: 'success' });
      void queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (err) =>
      toast({ title: t('No se pudo eliminar'), description: errorMessage(err), tone: 'error' }),
  });

  const feature = useMutation({
    mutationFn: ({ id, featured }: { id: string; featured: boolean }) =>
      api.admin.featureTeam(id, featured),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'teams'] }),
    onError: (err) =>
      toast({ title: t('No se pudo guardar'), description: errorMessage(err), tone: 'error' }),
  });

  return (
    <AdminPage
      title={t('Equipos')}
      description={t('Todos los equipos de la plataforma y su actividad.')}
    >
      <SearchBox
        value={q}
        onChange={(v) => {
          setQ(v);
          setPage(1);
        }}
        placeholder={t('Buscar equipo…')}
      />
      <DataTable
        head={[
          t('Equipo'),
          t('Propietario'),
          t('Miembros'),
          t('Canales'),
          t('Mensajes'),
          t('Tareas'),
          t('Última actividad'),
          '',
        ]}
        empty={teams.data?.items.length === 0}
      >
        {teams.data?.items.map((team) => (
          <tr key={team.id} className="hover:bg-surface-2/50">
            <td className="px-4 py-2.5">
              <div className="flex items-center gap-3">
                <Avatar id={team.id} name={team.name} shape="square" size="sm" />
                <div>
                  <Link
                    to={`/admin/teams/${team.id}`}
                    title={t('Abrir el equipo (solo lectura)')}
                    className="font-medium hover:text-accent hover:underline"
                  >
                    {team.name}
                  </Link>
                  {team.official && <Badge className="ml-2">{t('Oficial')}</Badge>}
                  {team.isPublic && !team.official && (
                    <Badge className="ml-2">{t('Público')}</Badge>
                  )}
                  <p className="text-xs text-muted">
                    {t('Creado {{when}}', { when: formatRelative(team.createdAt) })}
                  </p>
                </div>
              </div>
            </td>
            <td className="px-4 py-2.5 text-muted">{team.owner?.displayName ?? '—'}</td>
            <td className="px-4 py-2.5 tabular-nums">
              <button
                type="button"
                onClick={() => setViewing(team)}
                title={t('Ver sus miembros')}
                className="rounded-md px-1.5 py-0.5 text-accent hover:bg-accent-soft hover:underline"
              >
                {team.memberCount}
              </button>
            </td>
            <td className="px-4 py-2.5 tabular-nums">{team.channelCount}</td>
            <td className="px-4 py-2.5 tabular-nums">{team.messageCount}</td>
            <td className="px-4 py-2.5 tabular-nums">{team.taskCount}</td>
            <td className="px-4 py-2.5 text-muted">
              {team.lastActivityAt ? formatRelative(team.lastActivityAt) : '—'}
            </td>
            <td className="px-2 py-2.5 text-right whitespace-nowrap">
              <IconButton
                label={
                  team.featured ? t('Quitar de destacados') : t('Destacar en «Explorar servidores»')
                }
                size="sm"
                className={cn(team.featured && 'text-warning')}
                onClick={() => feature.mutate({ id: team.id, featured: !team.featured })}
              >
                <Star className={cn(team.featured && 'fill-warning')} />
              </IconButton>
              <IconButton
                label={t('Eliminar {{name}}', { name: team.name })}
                size="sm"
                className="hover:text-danger"
                onClick={() => {
                  if (
                    window.confirm(
                      t(
                        '¿Eliminar el equipo «{{name}}» con todos sus canales, mensajes, tareas y archivos? No se puede deshacer.',
                        { name: team.name },
                      ),
                    )
                  ) {
                    remove.mutate(team.id);
                  }
                }}
              >
                <Trash2 />
              </IconButton>
            </td>
          </tr>
        ))}
      </DataTable>
      {viewing && <TeamMembersDialog team={viewing} onClose={() => setViewing(null)} />}
      {teams.data && (
        <Pagination
          page={teams.data.page}
          pageSize={teams.data.pageSize}
          total={teams.data.total}
          onPage={setPage}
        />
      )}
    </AdminPage>
  );
}
